import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  ArrowRight,
} from 'lucide-react';
import { usePropertyStore } from '@/stores/propertyStore';
import { useAuthStore } from '@/stores/authStore';
import { calculatePortfolioMetrics } from '@/lib/calculations';
import { formatCurrency } from '@/lib/utils';
import { MetricCard } from '@/components/ui/metric-card';
import { WealthChart } from '@/components/charts/WealthChart';
import { ChartSettings } from '@/components/charts/ChartSettings';
import { PropertyCard } from '@/components/properties/PropertyCard';
import { PropertyFormModal } from '@/components/properties/PropertyFormModal';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';

export default function Dashboard() {
  const { properties, isLoading, fetchProperties } = usePropertyStore();
  const { user, isLoading: isAuthLoading } = useAuthStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);

  // Lade Daten nur wenn User authentifiziert ist
  useEffect(() => {
    // Warte auf Auth-Initialisierung
    if (isAuthLoading) return;

    // Kein User = keine Daten
    if (!user) return;

    // Verhindere doppeltes Laden
    if (hasFetched) return;

    setHasFetched(true);
    fetchProperties();
  }, [user, isAuthLoading, hasFetched, fetchProperties]);

  // Reset hasFetched wenn User sich ändert
  useEffect(() => {
    if (!user) {
      setHasFetched(false);
    }
  }, [user]);

  // Zeige Skeleton wenn Auth oder Daten noch laden
  const showSkeleton = isAuthLoading || (isLoading && properties.length === 0) || (!hasFetched && user);

  if (showSkeleton) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const metrics = calculatePortfolioMetrics(properties);
  const recentProperties = properties.slice(0, 3);
  const watchlistProperties = properties.filter(p => p.status === 'watchlist').slice(0, 3);

  if (properties.length === 0) {
    return (
      <>
        <EmptyState
          icon={Building2}
          title="Willkommen bei Fundament"
          description="Füge deine erste Immobilie hinzu, um loszulegen."
          action={{
            label: 'Erste Immobilie hinzufügen',
            onClick: () => setModalOpen(true),
          }}
        />
        <PropertyFormModal open={modalOpen} onOpenChange={setModalOpen} />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Portfolio Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Portfolio-Wert"
          value={formatCurrency(metrics.totalValue)}
        />
        <MetricCard
          label="Eigenkapital"
          value={formatCurrency(metrics.totalEquity)}
        />
        <MetricCard
          label="Cashflow / Monat"
          value={formatCurrency(metrics.totalCashflow)}
          trend={metrics.totalCashflow >= 0 ? 'positive' : 'negative'}
        />
        <MetricCard
          label="Objekte"
          value={`${metrics.propertyCount}`}
          subValue={`+ ${metrics.watchlistCount} Watchlist`}
        />
      </div>

      {/* Wealth Chart */}
      <div className="grid gap-6 lg:grid-cols-4">
        <div className="lg:col-span-3 p-5 rounded-lg border border-border bg-card">
          <h2 className="text-lg font-medium mb-4">Vermögensentwicklung</h2>
          <WealthChart properties={properties} />
        </div>
        <div className="p-5 rounded-lg border border-border bg-card">
          <ChartSettings />
        </div>
      </div>

      {/* Recent Properties */}
      {recentProperties.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Zuletzt bearbeitet</h2>
            <Link
              to="/properties"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              Alle anzeigen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recentProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        </div>
      )}

      {/* Watchlist */}
      {watchlistProperties.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Auf der Watchlist</h2>
            <Link
              to="/properties?status=watchlist"
              className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              Alle anzeigen
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {watchlistProperties.map((property) => (
              <PropertyCard key={property.id} property={property} />
            ))}
          </div>
        </div>
      )}

      <PropertyFormModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
